import './polyfills';

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { isProdMode, versionInfo, amBaseUrl } from './config';
import {
  StorageV2Service,
  StorageService,
  MultipleTranslatePoHttpLoader,
  TextInputModule,
  AppGuard,
  ShellV2Module,
  ActionMenuV2Module,
  ButtonV2Module,
  ButtonModule,
  CheckboxV2Module,
  ModalModule,
  SideDrawerModule,
  AssetsModule,
  UnitsModule,
  PagesV2Module,
  ChipV2Module,
  IconV2Module,
  ToggleV2Module,
  InputV2Module,
  SelectV2Module,
  RadioV2Module,
  GridV2Module,
  SpinnerModule,
  OutputV2Module,
  NotificationsV2Module,
  DateV2Module,
  UserRightsModule,
  PipesModule,
  WorkflowModule,
  ShellV2Component,
} from '@schenck/rotec-components';
import { SpinnerService } from './services/spinner/spinner.service';
import { apiProviders } from './api';
import { serviceProviders } from './services';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ErrorInterceptor } from './services/interceptors/error.interceptor';
import { ServerErrorInterceptor } from './services/interceptors/server-error.interceptor';
import { ServerErrorGuard } from './services/guards/server-error.guard';
import { CommonModule, registerLocaleData } from '@angular/common';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from './environment/environment';
import { provideAnimations } from '@angular/platform-browser/animations';
import { withHashLocation, provideRouter, Routes } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import { AppGuard as LocalAppGuard } from './services/guards/app.guard';
import { routes } from './routes';
import { UserListGridComponent } from './pages/user-list/user-list.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ModalsModule } from './components/modals/modals.module';
import { ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { bootstrapApplication } from '@angular/platform-browser';

import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import esLocale from '@angular/common/locales/es';
import frLocale from '@angular/common/locales/fr';
import itLocale from '@angular/common/locales/it';
import nlLocale from '@angular/common/locales/nl';
import svLocale from '@angular/common/locales/sv';
import plLocale from '@angular/common/locales/pl';
import trLocale from '@angular/common/locales/tr';
import zhLocale from '@angular/common/locales/zh';
import huLocale from '@angular/common/locales/hu';
import csLocale from '@angular/common/locales/cs';
import koLocale from '@angular/common/locales/ko';
import jaLocale from '@angular/common/locales/ja';
import ptLocale from '@angular/common/locales/pt';

registerLocaleData(deLocale, 'de');
registerLocaleData(enLocale, 'en');
registerLocaleData(esLocale, 'es');
registerLocaleData(frLocale, 'fr');
registerLocaleData(itLocale, 'it');
registerLocaleData(nlLocale, 'nl');
registerLocaleData(svLocale, 'sv');
registerLocaleData(plLocale, 'pl');
registerLocaleData(trLocale, 'tr');
registerLocaleData(zhLocale, 'zh');
registerLocaleData(huLocale, 'hu');
registerLocaleData(csLocale, 'cs');
registerLocaleData(koLocale, 'ko');
registerLocaleData(jaLocale, 'ja');
registerLocaleData(ptLocale, 'pt');

function HttpPoLoaderFactory(http: HttpClient) {
  return new MultipleTranslatePoHttpLoader(http, [
    { prefix: 'assets/rotec-locales/', suffix: '.po' },
    { prefix: 'assets/locales/', suffix: '.po' },
  ]);
}

const appRoutes: Routes = [
  {
    path: '',
    component: PagesComponent,
    canActivate: [AppGuard, LocalAppGuard],
    children: [
      { path: routes.userList.url, component: UserListGridComponent },
      {
        path: '',
        redirectTo: routes.userList.url,
        pathMatch: 'full',
      },
    ],
  },
  { path: '**', redirectTo: routes.userList.url },
];

if (isProdMode) {
  enableProdMode();
}

bootstrapApplication(ShellV2Component, {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpPoLoaderFactory,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      CommonModule,
      TextInputModule,
      StoreModule.forRoot({}),
      EffectsModule.forRoot([]),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
        connectInZone: true,
      }),
      DragDropModule,
      // Rotec-Components Library
      ShellV2Module.forRoot({
        isProduction: isProdMode,
        appLogoAndName: {
          name: 'User Manager',
          logoUrl: './assets/img/usermanager.png',
        },
        versionInfo,
        msalConfig: environment.msalConfig,
        azureEndpoint: environment.azureEndpoint,
        baseUrl: environment.apiBaseUrl,
        usBaseUrl: environment.usBaseUrl,
      }),
      ActionMenuV2Module,
      ButtonV2Module,
      ButtonModule,
      CheckboxV2Module,
      ModalModule.forRoot(),
      SideDrawerModule.forRoot(),
      AssetsModule.forRoot(amBaseUrl),
      UnitsModule.forRoot(),
      PagesV2Module,
      ModalsModule,
      ChipV2Module,
      IconV2Module,
      ToggleV2Module,
      InputV2Module,
      SelectV2Module,
      ReactiveFormsModule,
      RadioV2Module,
      MatRadioModule,
      GridV2Module,
      SpinnerModule.forRoot(),
      StoreModule,
      OutputV2Module,
      NotificationsV2Module,
      DateV2Module,
      UserRightsModule.forRoot(environment.umBaseUrl),
      PipesModule,
      WorkflowModule
    ),
    StorageV2Service,
    StorageService,
    SpinnerService,
    ...apiProviders,
    ...serviceProviders,
    ...[
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ServerErrorInterceptor,
        multi: true,
      },
    ],
    ServerErrorGuard,
    provideAnimations(),
    provideRouter(appRoutes, withHashLocation()),
  ],
}).catch((err) => console.log(err));
